import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { backend } from '@/api/backend';
import { ApiError, newIdempotencyKey } from '@/api/http';
import { registerBody, scopeLabel, whyNotRegistrable } from '@/pages/registration';

/**
 * Make one folder its own scope.
 *
 * The folder tree is the natural picker for this and the only one that exists:
 * for a registered scope the collections read gives the tenant's own filing,
 * and for an unregistered bucket there is no delimiter listing to offer. So
 * the path is register the bucket, let discovery run, then promote whichever
 * folder is the thing you actually care about.
 *
 * A promoted folder is a SEPARATE scope, not a filter on this one. It gets its
 * own discovery, walk, package and handover, and it cannot see the rest of the
 * bucket. That is the point: a bucket holding a 20,000-file repository export
 * beside a mailbox produces one walk over both, and two scopes produce two
 * reports each describing one system.
 */
export function WorkOnFolder({
  scope,
  prefix,
}: {
  scope: { name: string; accountId: string | null; region: string | null };
  prefix: string;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const asScope = { ...scope, prefix };

  const promote = async () => {
    const why = whyNotRegistrable(asScope);
    if (why) {
      setNote(why);
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const r = await backend.registerBucket(registerBody(asScope), newIdempotencyKey());
      void qc.invalidateQueries({ queryKey: ['live-buckets'] });
      navigate(`/buckets/${r.bucket.id}`);
    } catch (e) {
      // A scope that already exists is refused by the unique key, and that is
      // an answer rather than a fault: it means the folder is already its own.
      setNote(e instanceof ApiError ? e.message : 'The API did not respond.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => void promote()}
        disabled={busy}
        title={`Register ${scopeLabel(scope.name, prefix)} as its own scope, with its own walk and handover`}
        className="flex-none rounded border border-line px-2 py-0.5 text-caption text-ink-4 transition-colors duration-fast hover:bg-hov hover:text-acc-2 disabled:opacity-50"
      >
        {busy ? 'registering…' : 'work on this'}
      </button>
      {note && (
        <span role="alert" className="flex-none text-caption text-warn">
          {note}
        </span>
      )}
    </>
  );
}
